/**
 * The single `requestAnimationFrame` scheduler.
 *
 * There is exactly one rAF loop in this client. WS frames never call
 * `setState`; they mutate a per-channel buffer (`lib/buffer.ts`) and one flush
 * per frame bumps the external stores components read through
 * `useSyncExternalStore` (plan §2.2). At the 131 f/s peak the idiomatic React
 * path drops frames on exactly the five seconds the user is watching.
 *
 * Two lanes, because plan §2.4 is a hard invariant: **the countdown and
 * `<ResolutionPanel>` hold their own rAF budget and are never starved by book
 * churn.** `critical` callbacks run first and always run to completion.
 * `default` callbacks share a time budget and, when they exceed it, resume from
 * where they stopped on the next frame — so a slow book task delays other book
 * tasks by a frame and never touches the clock.
 *
 * Framework-free and injectable: the whole thing is testable with a fake frame
 * pump and no DOM.
 */

export type RafLane = 'critical' | 'default'

export interface FrameInfo {
  /** The timestamp the host passed to the rAF callback. */
  timeMs: number
  /** Since the previous flush. `0` on the first frame. */
  deltaMs: number
  /** Monotonic frame counter, from 1. */
  frame: number
}

export type FrameCallback = (frame: FrameInfo) => void

export interface RafSchedulerOptions {
  requestFrame?: (cb: (timeMs: number) => void) => number
  cancelFrame?: (handle: number) => void
  /** Monotonic clock used to police the `default` lane's budget. */
  monoNow?: () => number
  /**
   * Milliseconds of a 16ms frame the `default` lane may consume. The remainder
   * is headroom for paint — overrunning it is what turns 60fps into 45.
   */
  defaultLaneBudgetMs?: number
  /** Reported rather than thrown: a subscriber that throws must not stop the loop. */
  onError?: (error: unknown, lane: RafLane) => void
}

interface Subscription {
  fn: FrameCallback
  lane: RafLane
  once: boolean
}

export const DEFAULT_LANE_BUDGET_MS = 8

export class RafScheduler {
  readonly #requestFrame: (cb: (timeMs: number) => void) => number
  readonly #cancelFrame: (handle: number) => void
  readonly #monoNow: () => number
  readonly #budgetMs: number
  readonly #onError: (error: unknown, lane: RafLane) => void

  readonly #critical = new Set<Subscription>()
  readonly #default: Subscription[] = []

  #handle: number | null = null
  #frame = 0
  #lastFrameMs: number | null = null
  /** Round-robin cursor into the default lane, so a deferred tail is never the same tail twice. */
  #cursor = 0
  #overrunFrames = 0

  constructor(options: RafSchedulerOptions = {}) {
    this.#requestFrame =
      options.requestFrame ?? ((cb) => globalThis.requestAnimationFrame(cb))
    this.#cancelFrame = options.cancelFrame ?? ((h) => globalThis.cancelAnimationFrame(h))
    this.#monoNow = options.monoNow ?? (() => performance.now())
    this.#budgetMs = options.defaultLaneBudgetMs ?? DEFAULT_LANE_BUDGET_MS
    this.#onError = options.onError ?? (() => {})
  }

  /** Frames in which the default lane hit its budget and deferred work. A
   *  climbing count is the early warning that the book work needs canvas. */
  get overrunFrames(): number {
    return this.#overrunFrames
  }

  get running(): boolean {
    return this.#handle !== null
  }

  get subscriberCount(): number {
    return this.#critical.size + this.#default.length
  }

  subscribe(fn: FrameCallback, lane: RafLane = 'default'): () => void {
    return this.#add({ fn, lane, once: false })
  }

  /** Run `fn` on the next frame only. The flush that drains the channel
   *  buffers is scheduled this way: one flush per frame, however many frames
   *  arrived in between. */
  scheduleOnce(fn: FrameCallback, lane: RafLane = 'default'): () => void {
    return this.#add({ fn, lane, once: true })
  }

  #add(sub: Subscription): () => void {
    if (sub.lane === 'critical') this.#critical.add(sub)
    else this.#default.push(sub)
    this.#ensureRunning()
    return () => this.#remove(sub)
  }

  #remove(sub: Subscription): void {
    if (sub.lane === 'critical') {
      this.#critical.delete(sub)
      return
    }
    const i = this.#default.indexOf(sub)
    if (i >= 0) {
      this.#default.splice(i, 1)
      if (this.#cursor > i) this.#cursor--
    }
  }

  stop(): void {
    if (this.#handle !== null) {
      this.#cancelFrame(this.#handle)
      this.#handle = null
    }
  }

  #ensureRunning(): void {
    if (this.#handle === null && this.subscriberCount > 0) {
      this.#handle = this.#requestFrame((t) => this.#tick(t))
    }
  }

  #tick(timeMs: number): void {
    this.#handle = null
    this.#frame++
    const info: FrameInfo = {
      timeMs,
      deltaMs: this.#lastFrameMs === null ? 0 : timeMs - this.#lastFrameMs,
      frame: this.#frame,
    }
    this.#lastFrameMs = timeMs

    // The critical lane is unbudgeted by design. It is the countdown and the
    // resolution panel: a handful of cheap callbacks whose entire job is to be
    // on time, sixty times an hour, in front of a watching user.
    for (const sub of [...this.#critical]) {
      if (sub.once) this.#critical.delete(sub)
      this.#run(sub, info)
    }

    this.#runDefaultLane(info)
    this.#ensureRunning()
  }

  #runDefaultLane(info: FrameInfo): void {
    const pending = this.#default.length
    if (pending === 0) return
    const deadline = this.#monoNow() + this.#budgetMs

    let ran = 0
    let deferred = false
    while (ran < pending) {
      if (this.#default.length === 0) break
      if (this.#cursor >= this.#default.length) this.#cursor = 0
      const sub = this.#default[this.#cursor]
      if (!sub) break

      if (sub.once) this.#default.splice(this.#cursor, 1)
      else this.#cursor++
      this.#run(sub, info)
      ran++

      // Check after running, never before: a budget that can skip every
      // callback is a lane that silently stops working under load.
      if (ran < pending && this.#monoNow() >= deadline) {
        deferred = true
        break
      }
    }
    if (deferred) this.#overrunFrames++
  }

  #run(sub: Subscription, info: FrameInfo): void {
    try {
      sub.fn(info)
    } catch (error) {
      this.#onError(error, sub.lane)
    }
  }
}

/** The tab's scheduler. One loop, like the one socket and the one pooled
 *  connection — created lazily so importing this module costs nothing. */
let shared: RafScheduler | null = null

export function sharedScheduler(): RafScheduler {
  shared ??= new RafScheduler()
  return shared
}

/** Tests only. */
export function __resetSharedScheduler(): void {
  shared?.stop()
  shared = null
}
